import React from 'react';
import { Table, Form } from 'react-bootstrap';
import { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from "../helpers/Fetchwrapper";
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import { numberFormat } from '../helpers/GlobalHelper';

interface PendingDeliveryRow {
    idSalesOrders: number;
    salesOrderId: string | null;
    salesOrderDate: string;
    companyName: string | null;
    salesPerson: string | null;
    productName: string | null;
    packing: string | null;
    size: string | null;
    orderedQuantity: number | string;
    alreadyDelivered: number | string;
    pendingDelivery: number | string;
    isFreeProduct?: boolean | number | string;
}

function isFreeProductRow(line: PendingDeliveryRow): boolean {
    const v = line.isFreeProduct;
    return v === true || v === 1 || v === '1';
}

/** Consecutive rows with the same sales order id (matches API sort by date then order id). */
function groupRowsBySalesOrder(list: PendingDeliveryRow[]): PendingDeliveryRow[][] {
    const groups: PendingDeliveryRow[][] = [];
    for (const row of list) {
        const prev = groups[groups.length - 1];
        if (prev && prev[0].idSalesOrders === row.idSalesOrders) {
            prev.push(row);
        } else {
            groups.push([row]);
        }
    }
    return groups;
}

const PENDING_TYPE_OPTIONS = [
    { label: 'All Type', value: 'all' },
    { label: 'Fully Pending', value: 'full' },
    { label: 'Partial Pending', value: 'partial' },
];

const SalesOrdersPendingDeliveryReport: React.FC = () => {

    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [rows, setRows] = useState<PendingDeliveryRow[]>([]);
    const [formData, setFormData] = useState({
        idProjects: '',
        idCompanies: '',
        fromDate: '',
        toDate: '',
        pendingType: 'all',
    });

    const [projects, setProjects] = useState<{ label: string; value: string | number }[]>([]);
    const [project, setProject] = useState<{ label: string; value: string | number }>({ label: 'Select Project', value: '' });

    const [companies, setCompanies] = useState<{ label: string; value: string | number }[]>([]);
    const [company, setCompany] = useState<{ label: string; value: string | number } | null>({ label: 'All Clients', value: '' });

    const [pendingTypeOption, setPendingTypeOption] = useState(PENDING_TYPE_OPTIONS[0]);

    useEffect(() => {
        getProjects();
        getCompanies();
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            setProject(projects[0]);
            setFormData((prev) => ({
                ...prev,
                idProjects: String(projects[0].value),
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectObj = resp.data.map((item: any) => {
                    item.label = item.name;
                    item.value = item.id;
                    return item;
                });
                setProjects(projectObj);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const getCompanies = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/companies', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const companiesObj = resp.data.map((item: any) => {
                    item.label = item.name;
                    item.value = item.id;
                    return item;
                });
                setCompanies(companiesObj);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const handleProjectChange = (p: { label: string; value: string | number } | null) => {
        if (p) {
            setProject(p);
            setFormData({ ...formData, idProjects: String(p.value) });
        } else {
            setProject({ label: 'Select Project', value: '' });
            setFormData({ ...formData, idProjects: '' });
        }
    };

    const handleCompanyChange = (c: { label: string; value: string | number } | null) => {
        setCompany(c);
        setFormData({ ...formData, idCompanies: c?.value != null && c.value !== '' ? String(c.value) : '' });
    };

    const handlePendingTypeChange = (opt: { label: string; value: string } | null) => {
        const chosen = opt ?? PENDING_TYPE_OPTIONS[0];
        setPendingTypeOption(chosen);
        setFormData({ ...formData, pendingType: String(chosen.value) });
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });
    };

    const qtyDisplay = (value: number | string) => numberFormat(parseFloat(String(value ?? 0)));

    const rowGroups = useMemo(() => groupRowsBySalesOrder(rows), [rows]);

    const downloadQueryParams = new URLSearchParams();
    if (formData.idProjects) {
        downloadQueryParams.set('idProjects', formData.idProjects);
    }
    downloadQueryParams.set('pendingType', formData.pendingType);
    if (formData.idCompanies) {
        downloadQueryParams.set('idCompanies', formData.idCompanies);
    }
    if (formData.fromDate) {
        downloadQueryParams.set('fromDate', formData.fromDate);
    }
    if (formData.toDate) {
        downloadQueryParams.set('toDate', formData.toDate);
    }

    const handleViewReport = async () => {
        if (!formData.idProjects) {
            toast.error('Please select a project.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);
        try {
            const payload = {
                idProjects: formData.idProjects,
                pendingType: formData.pendingType,
                ...(formData.idCompanies ? { idCompanies: formData.idCompanies } : {}),
                ...(formData.fromDate ? { fromDate: formData.fromDate } : {}),
                ...(formData.toDate ? { toDate: formData.toDate } : {}),
            };
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'reports/sales-orders-pending-delivery',
                postRequestOptions(payload)
            );
            const data = await response.json();

            if (data.success) {
                setRows(data.data ?? []);
                setSearched(true);
            } else {
                setRows([]);
                setSearched(true);
                if (data.errors) {
                    for (const i in data.errors) {
                        toast(<strong>{(data.errors[i] as string[]).join(' ')}</strong>, {
                            type: 'error',
                            position: 'bottom-right',
                            autoClose: 4000,
                        });
                    }
                }
            }
        } catch (error) {
            console.error('Error submitting data:', error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    <Card.Title>Sales Orders Pending Delivery</Card.Title>
                    <Card.Text>
                        <div className="row">
                            <div className="col-md-2">
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className="required"></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-2">
                                <Form.Group className="mb-3" controlId="company">
                                    <Form.Label>Client <span className="text-muted">(optional)</span></Form.Label>
                                    <Select
                                        value={company}
                                        onChange={handleCompanyChange}
                                        options={[{ label: 'All Clients', value: '' }, ...companies]}
                                        menuPosition="fixed"
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-2">
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Date <span className="text-muted">(optional)</span></Form.Label>
                                    <Form.Control type="date" name="fromDate" value={formData.fromDate} onChange={handleChange} />
                                </Form.Group>
                            </div>
                            <div className="col-md-2">
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date <span className="text-muted">(optional)</span></Form.Label>
                                    <Form.Control type="date" name="toDate" value={formData.toDate} onChange={handleChange} />
                                </Form.Group>
                            </div>
                            <div className="col-md-2">
                                <Form.Group className="mb-3" controlId="pendingType">
                                    <Form.Label>Pending Type</Form.Label>
                                    <Select
                                        value={pendingTypeOption}
                                        onChange={handlePendingTypeChange}
                                        options={PENDING_TYPE_OPTIONS}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={false}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-1">
                                <div className="form-control-sm ps-0 mt-7">
                                    <button type="button" className="btn btn-sm btn-primary w-100 p-3" onClick={handleViewReport} disabled={loading}>
                                        {loading && <span className="spinner-border spinner-border-sm me-1"></span>}
                                        <span>Generate</span>
                                    </button>
                                </div>
                            </div>
                            <div className="col-md-1">
                                <div className="form-control-sm ps-0 mt-7">
                                    <a
                                        className="btn btn-sm btn-success w-100 p-3"
                                        href={`${process.env.REACT_APP_BACKEND}sales_orders_pending_delivery_download?${downloadQueryParams.toString()}`}
                                    >
                                        <i className="fas fa-download"></i> Excel
                                    </a>
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <div className="row">
                <div className="col-12">
                    <Table responsive bordered hover>
                        <thead>
                            <tr>
                                <th className="text-center">SL</th>
                                <th className="text-center">Sales Order ID</th>
                                <th className="text-center">Sales Order Date</th>
                                <th className="text-center">Client</th>
                                <th className="text-center">Sales Person</th>
                                <th className="text-center">Product</th>
                                <th className="text-center">Packing</th>
                                <th className="text-center">Size</th>
                                <th className="text-end">Ordered Quantity</th>
                                <th className="text-end">Already Delivered</th>
                                <th className="text-end">Pending Delivery</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length > 0 ? (
                                rowGroups.map((group, groupIdx) => {
                                    const span = group.length;
                                    const meta = group[0];
                                    return (
                                        <React.Fragment key={`${meta.idSalesOrders}-${groupIdx}`}>
                                            {group.map((line, lineIdx) => (
                                                <tr key={`${meta.idSalesOrders}-${groupIdx}-line-${lineIdx}`}>
                                                    {lineIdx === 0 && (
                                                        <>
                                                            <td rowSpan={span} className="text-center align-middle">{groupIdx + 1}</td>
                                                            <td rowSpan={span} className="text-center align-middle">
                                                                <Link to={`/sales-order/${meta.idSalesOrders}`}>
                                                                    {meta.salesOrderId ? meta.salesOrderId : meta.idSalesOrders}
                                                                </Link>
                                                            </td>
                                                            <td rowSpan={span} className="text-center align-middle">{meta.salesOrderDate}</td>
                                                            <td rowSpan={span} className="align-middle">{meta.companyName ?? ''}</td>
                                                            <td rowSpan={span} className="align-middle">{meta.salesPerson ?? ''}</td>
                                                        </>
                                                    )}
                                                    <td>
                                                        <span>{line.productName ?? ''}</span>
                                                        {isFreeProductRow(line) && (
                                                            <span className="text-muted small d-block">Free Products</span>
                                                        )}
                                                    </td>
                                                    <td>{line.packing ?? ''}</td>
                                                    <td>{line.size ?? ''}</td>
                                                    <td className="text-end">{qtyDisplay(line.orderedQuantity)}</td>
                                                    <td className="text-end">{qtyDisplay(line.alreadyDelivered)}</td>
                                                    <td className="text-end">{qtyDisplay(line.pendingDelivery)}</td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={11} className="text-center">
                                        {searched ? 'No pending delivery rows found.' : 'Choose filters and click Generate.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
        </>
    );
};

export default SalesOrdersPendingDeliveryReport;
